/**
 * Users: the device's local accounts, read live, and forms to create one and change an existing
 * one's password, state, groups and existence.
 *
 * Like the event log, the list is a live round trip to the machine, so it loads on this tab's first
 * reveal rather than at page open. Windows-only - the shell hides the tab on a Synology, whose users
 * belong to DSM's Control Panel.
 *
 * The form sends a user name, a password and a handful of switches; the AGENT creates the account
 * through the Windows account API, so nothing typed here becomes a command line. The machine's own
 * password policy is the judge of the password, and its refusal comes back verbatim - the CRM does
 * not guess at a policy only the device knows.
 *
 * The password boxes are emptied after a successful create and when the form closes, so a password
 * does not sit in a hidden control for the life of the page.
 */
import type { Control } from 'webwin';
import * as crm from '../../api/crm.js';
import { describeError } from '../../api/http.js';
import type { LocalUserDto } from '../../contracts.gen.js';
import { join, localDateTime, plural } from '../../format.js';
import { CardList } from '../../ui/CardList.js';
import { chip } from '../../ui/Chip.js';
import { confirm } from '../../ui/dialogs.js';
import { BoundedScroller } from '../../ui/Scroller.js';
import { StatusLine } from '../../ui/StatusLine.js';
import { button, buttonRow, card, checkBox, field, fieldRow, fillWith, FONT, GAP, h2, hstack, inlineRow, muted, passwordBox, splitRow, stackOf, text, textBox, value, vstack } from '../../ui/layout.js';
import type { DeviceTab, DeviceTabContext } from './context.js';

export interface UsersTab extends DeviceTab {
  applyPresence(): void;
}

export function usersTab(dc: DeviceTabContext): UsersTab {
  let busy = false;
  let loaded = false;

  const panel = vstack({ spacing: GAP.md, hAlign: 'stretch', autoWidth: false });
  const status = new StatusLine();

  // ---- list ----
  const rows = new CardList<LocalUserDto>({
    key: u => u.name.toLowerCase(),
    version: u => JSON.stringify(u),
    build: u => buildRow(u),
    spacing: GAP.sm
  });
  const scroller = new BoundedScroller(420);
  scroller.content.controls.add(rows.panel);

  const waiting = muted('The list is read from the device when this tab opens, so it needs to be online.');
  const refreshButton = button('Refresh', () => void load());
  const newButton = button('New user', () => openForm(true), true);

  const listHeader = splitRow({ align: 'center' });
  fillWith(listHeader, text('Local accounts', { font: FONT.bold }));
  listHeader.controls.add(stackOf(buttonRow(), newButton, refreshButton));

  // ---- create form ----
  const nameBox = textBox();
  const fullNameBox = textBox();
  const descriptionBox = textBox();
  const passwordInput = passwordBox();
  const confirmInput = passwordBox();
  const adminCheck = checkBox('Administrator - full control of this machine');
  const rdpCheck = checkBox('Allow Remote Desktop sign-in');
  // Checked by default: whenever a technician chose the password, the person it is for should
  // replace it. The two expiry switches contradict each other, so ticking one clears the other.
  const mustChangeCheck = checkBox('Must change password at first sign-in', true, {
    onChange: () => {
      if (mustChangeCheck.checked) neverExpiresCheck.checked = false;
    }
  });
  const neverExpiresCheck = checkBox('Password never expires', false, {
    onChange: () => {
      if (neverExpiresCheck.checked) mustChangeCheck.checked = false;
    }
  });

  const createButton = button('Create user', () => void create(), true);
  const cancelButton = button('Cancel', () => openForm(false));
  const formStatus = new StatusLine();

  const form = card({ tint: 'alt' });
  form.controls.add(
    stackOf(
      vstack({ spacing: GAP.sm, hAlign: 'stretch', autoWidth: false }),
      text('New local user', { font: FONT.bold }),
      fieldRow(field('User name', nameBox), field('Full name (optional)', fullNameBox)),
      field('Description (optional)', descriptionBox),
      fieldRow(field('Password', passwordInput), field('Confirm password', confirmInput)),
      stackOf(vstack({ spacing: GAP.xs }), adminCheck, rdpCheck, mustChangeCheck, neverExpiresCheck),
      stackOf(buttonRow(), createButton, cancelButton),
      formStatus.label,
      text(
        "Created on the device by the agent through Windows' account API - nothing typed here runs as a " +
          "command. The machine's password policy decides whether the password is acceptable. The account " +
          "and its options are recorded in the device's history; the password is not recorded anywhere.",
        { role: 'muted', font: FONT.tiny, wrap: true }
      )
    )
  );
  form.visible = false;

  // ---- reset-password form ----
  // One form shared by every row, opened by the row's own button, rather than a form per row: a
  // password typed into a row that the list then rebuilt on a refresh would vanish mid-entry.
  let resetUser: LocalUserDto | null = null;
  const resetTitle = text('', { font: FONT.bold });
  const resetPasswordInput = passwordBox();
  const resetConfirmInput = passwordBox();
  const resetMustChangeCheck = checkBox('Must change password at next sign-in', true);
  const resetUnlockCheck = checkBox('Unlock the account if it is locked out', true);
  const resetNote = text('', { role: 'muted', font: FONT.small, wrap: true });
  const resetButton = button('Reset password', () => void resetPassword(), true);
  const resetStatus = new StatusLine();

  const resetForm = card({ tint: 'alt' });
  resetForm.controls.add(
    stackOf(
      vstack({ spacing: GAP.sm, hAlign: 'stretch', autoWidth: false }),
      resetTitle,
      fieldRow(field('New password', resetPasswordInput), field('Confirm password', resetConfirmInput)),
      stackOf(vstack({ spacing: GAP.xs }), resetMustChangeCheck, resetUnlockCheck),
      resetNote,
      stackOf(buttonRow(), resetButton, button('Cancel', () => openReset(null))),
      resetStatus.label,
      text(
        "Set on the device by the agent through Windows' account API. The machine's password policy decides " +
          "whether the password is acceptable. The reset is recorded in the device's history; the password is not.",
        { role: 'muted', font: FONT.tiny, wrap: true }
      )
    )
  );
  resetForm.visible = false;

  // ---- delete form ----
  // Typed confirmation, like the customer purge: a dialog that needs only OK is exactly the kind that
  // gets OK'd, and this one cannot be undone. The profile option is OFF by default - it deletes the
  // person's files, which is a second irreversible decision, not a detail of the first.
  let deleteUser: LocalUserDto | null = null;
  const deleteTitle = text('', { font: FONT.bold });
  const deleteNameBox = textBox();
  const deleteProfileCheck = checkBox('Also delete the profile folder (C:\\Users\\…) and everything in it', false);
  const deleteNote = text('', { role: 'muted', font: FONT.small, wrap: true });
  const deleteButton = button('Delete user', () => void removeUser());
  const deleteStatus = new StatusLine();
  const deleteForm = card({ tint: 'danger' });
  deleteForm.controls.add(
    stackOf(
      vstack({ spacing: GAP.sm, hAlign: 'stretch', autoWidth: false }),
      deleteTitle,
      deleteNote,
      field('Type the account name to confirm', deleteNameBox),
      deleteProfileCheck,
      stackOf(buttonRow(), deleteButton, button('Cancel', () => openDelete(null))),
      deleteStatus.label
    )
  );
  deleteForm.visible = false;

  // ---- change-groups form ----
  // Shared by every row for the same reason as the reset form: the list rebuilds on every read.
  let groupsUser: LocalUserDto | null = null;
  const groupsTitle = text('', { font: FONT.bold });
  const groupsAdminCheck = checkBox('Administrator - full control of this machine');
  const groupsRdpCheck = checkBox('Allow Remote Desktop sign-in');
  const groupsButton = button('Save groups', () => void saveGroups(), true);
  const groupsStatus = new StatusLine();
  const groupsForm = card({ tint: 'alt' });
  groupsForm.controls.add(
    stackOf(
      vstack({ spacing: GAP.sm, hAlign: 'stretch', autoWidth: false }),
      groupsTitle,
      stackOf(vstack({ spacing: GAP.xs }), groupsAdminCheck, groupsRdpCheck),
      stackOf(buttonRow(), groupsButton, button('Cancel', () => openGroups(null))),
      groupsStatus.label,
      text(
        'Every account stays in Users. A change takes effect at the next sign-in - a session already open ' +
          'keeps the rights it started with. The change is recorded in the device\'s history.',
        { role: 'muted', font: FONT.tiny, wrap: true }
      )
    )
  );
  groupsForm.visible = false;

  const body = card();
  body.controls.add(
    stackOf(
      vstack({ spacing: GAP.sm, hAlign: 'stretch', autoWidth: false }),
      listHeader,
      form,
      resetForm,
      groupsForm,
      deleteForm,
      status.label,
      waiting,
      scroller
    )
  );

  panel.controls.addRange([h2('Users'), body]);

  // ---------------------------------------------------------------
  //  Rows
  // ---------------------------------------------------------------

  function buildRow(user: LocalUserDto): Control {
    const heading = inlineRow();
    heading.controls.add(value(user.name, { font: FONT.bold }));
    if (user.fullName) heading.controls.add(muted(user.fullName));
    if (user.isAdministrator) heading.controls.add(chip('Administrator', 'warn'));
    if (!user.enabled) heading.controls.add(chip('Disabled', 'danger'));
    if (user.isLockedOut) heading.controls.add(chip('Locked out', 'danger'));
    if (user.isBuiltIn) heading.controls.add(chip('Built-in'));

    const details = join(
      [
        user.description ?? '',
        user.groups.length ? 'Groups: ' + user.groups.join(', ') : '',
        user.lastLogonUtc ? 'Last sign-in ' + localDateTime(user.lastLogonUtc) : 'Never signed in',
        user.passwordNeverExpires ? 'Password never expires' : ''
      ],
      '  ·  '
    );

    const left = stackOf(
      vstack({ spacing: GAP.xs, hAlign: 'stretch', autoWidth: false }),
      heading,
      text(details, { role: 'muted', font: FONT.small, wrap: true })
    );

    // Always enabled: the row is built once per load, so a presence change would leave a disabled
    // button stale. The form it opens is gated, and the handler re-checks.
    const row = splitRow({ spacing: GAP.md, align: 'start' });
    fillWith(row, left);
    row.controls.add(
      stackOf(buttonRow(), button(user.enabled ? 'Disable' : 'Enable', () => void setEnabled(user, !user.enabled)), button('Reset password', () => openReset(user)), button('Groups', () => openGroups(user)), button('Delete', () => openDelete(user)))
    );
    return row;
  }

  // ---------------------------------------------------------------
  //  Loading
  // ---------------------------------------------------------------

  async function load(): Promise<void> {
    if (busy) return;
    busy = true;
    loaded = true;
    applyPresence();
    status.busy('Reading the local accounts…');
    waiting.visible = false;
    dc.relayout();

    try {
      const result = await crm.listDeviceUsers(dc.deviceId, dc.ctx.dispose.signal());
      if (dc.ctx.cancelled()) return;
      if (!result.success) {
        rows.set([]);
        status.error(result.error ?? 'Could not read the local accounts.');
        return;
      }
      rows.set(result.users);
      scroller.fit();
      status.set(`${plural(result.users.length, 'local account')} on ${result.machineName}.`);
    } catch (error) {
      if (!dc.ctx.cancelled()) {
        rows.set([]);
        status.error(describeError(error));
      }
    } finally {
      busy = false;
      applyPresence();
      dc.relayout();
    }
  }

  // ---------------------------------------------------------------
  //  Creating
  // ---------------------------------------------------------------

  function clearPasswords(): void {
    passwordInput.text = '';
    confirmInput.text = '';
  }

  function openForm(open: boolean): void {
    form.visible = open;
    newButton.visible = !open;
    formStatus.clear();
    if (!open) clearPasswords();
    else {
      openReset(null);
      openDelete(null);
      openGroups(null);
    }
    dc.relayout();
  }

  async function create(): Promise<void> {
    if (busy) return;

    const userName = nameBox.text.trim();
    if (!userName) {
      formStatus.error('Enter a user name.');
      dc.relayout();
      return;
    }
    if (!passwordInput.text) {
      formStatus.error('Enter a password.');
      dc.relayout();
      return;
    }
    // Checked here because only here can it be: the server never sees the second copy.
    if (passwordInput.text !== confirmInput.text) {
      formStatus.error('The two passwords do not match.');
      dc.relayout();
      return;
    }

    const machine = dc.device()?.name ?? 'this device';
    const ok = await confirm(dc.ctx.desktop, {
      title: 'Create local user',
      message: adminCheck.checked
        ? `Create ${userName} as an ADMINISTRATOR on ${machine}?\n\nIt will have full control of the ` +
          'machine, including every other account on it.'
        : `Create the local account ${userName} on ${machine}?`
    });
    if (!ok || dc.ctx.cancelled()) return;

    let created: string | null = null;
    busy = true;
    applyPresence();
    formStatus.busy(`Creating ${userName}…`);
    dc.relayout();

    try {
      const result = await crm.createDeviceUser(
        dc.deviceId,
        {
          userName,
          fullName: fullNameBox.text.trim() || null,
          description: descriptionBox.text.trim() || null,
          password: passwordInput.text,
          administrator: adminCheck.checked,
          remoteDesktop: rdpCheck.checked,
          mustChangePassword: mustChangeCheck.checked,
          passwordNeverExpires: neverExpiresCheck.checked
        },
        dc.ctx.dispose.signal()
      );
      if (dc.ctx.cancelled()) return;

      if (!result.success) {
        formStatus.error(result.error ?? 'The device refused to create the account.');
        return;
      }

      nameBox.text = '';
      fullNameBox.text = '';
      descriptionBox.text = '';
      adminCheck.checked = false;
      rdpCheck.checked = false;
      mustChangeCheck.checked = true;
      neverExpiresCheck.checked = false;
      clearPasswords();
      openForm(false);
      created = result.user?.name ?? userName;
    } catch (error) {
      if (!dc.ctx.cancelled()) formStatus.error(describeError(error));
    } finally {
      busy = false;
      applyPresence();
      dc.relayout();
    }

    // The list is re-read rather than patched, so it shows the account as the device has it. The
    // outcome is written AFTER the reload, which sets its own status while running.
    if (created !== null && !dc.ctx.cancelled()) {
      await load();
      status.ok(`Created ${created}. ${status.label.text}`);
    }
  }

  // ---------------------------------------------------------------
  //  Resetting a password
  // ---------------------------------------------------------------

  function clearResetPasswords(): void {
    resetPasswordInput.text = '';
    resetConfirmInput.text = '';
  }

  // ---------------------------------------------------------------
  //  Deleting
  // ---------------------------------------------------------------

  function openDelete(user: LocalUserDto | null): void {
    if (user !== null && !dc.isOnline()) {
      status.error('The device is offline.');
      dc.relayout();
      return;
    }
    deleteUser = user;
    deleteForm.visible = user !== null;
    deleteStatus.clear();
    deleteNameBox.text = '';
    deleteProfileCheck.checked = false;
    if (user !== null) {
      openForm(false);
      openReset(null);
      openGroups(null);
      deleteTitle.text = `Delete ${user.name}`;
      deleteNote.text =
        'This cannot be undone. The account is removed for good - creating one with the same name later makes ' +
        'a different account that owns none of its files or permissions. ' +
        (user.isBuiltIn ? 'Windows will not delete its own built-in accounts; disable this one instead. ' : '') +
        'Without the profile option, its files stay in C:\\Users and can be recovered by an administrator.';
    }
    dc.relayout();
  }

  async function removeUser(): Promise<void> {
    if (busy || deleteUser === null) return;
    const user = deleteUser;
    // Exact, as the server and the agent check it: typing it is the proof it was read.
    if (deleteNameBox.text.trim() !== user.name) {
      deleteStatus.error(`Type ${user.name} exactly to confirm.`);
      dc.relayout();
      return;
    }

    let done: string | null = null;
    busy = true;
    applyPresence();
    deleteStatus.busy(`Deleting ${user.name}…`);
    dc.relayout();

    try {
      const result = await crm.deleteDeviceUser(
        dc.deviceId,
        { userName: user.name, confirmName: deleteNameBox.text.trim(), deleteProfile: deleteProfileCheck.checked },
        dc.ctx.dispose.signal()
      );
      if (dc.ctx.cancelled()) return;
      if (!result.success) {
        deleteStatus.error(result.error ?? 'The device refused to delete the account.');
        return;
      }
      openDelete(null);
      done = result.profileDeleted
        ? `Deleted ${user.name} and its profile.`
        : result.profileError
          ? `Deleted ${user.name}. ${result.profileError}`
          : `Deleted ${user.name}.`;
    } catch (error) {
      if (!dc.ctx.cancelled()) deleteStatus.error(describeError(error));
    } finally {
      busy = false;
      applyPresence();
      dc.relayout();
    }

    if (done !== null && !dc.ctx.cancelled()) {
      await load();
      status.ok(`${done} ${status.label.text}`);
    }
  }

  function openReset(user: LocalUserDto | null): void {
    if (user !== null && !dc.isOnline()) {
      status.error('The device is offline.');
      dc.relayout();
      return;
    }
    resetUser = user;
    resetForm.visible = user !== null;
    resetStatus.clear();
    clearResetPasswords();
    if (user !== null) {
      // Opening one form closes the other: two password forms on one card is how a password gets
      // typed into the wrong one.
      openForm(false);
      openDelete(null);
      openGroups(null);
      resetTitle.text = `Reset the password of ${user.name}`;
      // Windows has no expiry to apply to a password that never expires, and the agent would refuse
      // it - so the option is cleared and greyed rather than offered and then rejected.
      resetMustChangeCheck.checked = !user.passwordNeverExpires;
      resetMustChangeCheck.enabled = !user.passwordNeverExpires;
      resetUnlockCheck.checked = true;
      resetNote.text = join(
        [
          user.passwordNeverExpires ? 'This account is set to "password never expires", so it cannot be made to change it.' : '',
          user.isLockedOut ? 'This account is locked out.' : ''
        ],
        ' '
      );
      resetNote.visible = resetNote.text.length > 0;
    }
    dc.relayout();
  }

  async function resetPassword(): Promise<void> {
    if (busy || resetUser === null) return;
    const user = resetUser;

    if (!resetPasswordInput.text) {
      resetStatus.error('Enter the new password.');
      dc.relayout();
      return;
    }
    // Checked here because only here can it be: the server never sees the second copy.
    if (resetPasswordInput.text !== resetConfirmInput.text) {
      resetStatus.error('The two passwords do not match.');
      dc.relayout();
      return;
    }

    const machine = dc.device()?.name ?? 'this device';
    const ok = await confirm(dc.ctx.desktop, {
      title: 'Reset password',
      message:
        `Reset the password of ${user.name} on ${machine}?\n\nThe old password stops working at once. ` +
        'Anything that signs in with it - a mapped drive, a scheduled task, a service - will fail until updated.'
    });
    if (!ok || dc.ctx.cancelled()) return;

    let done: string | null = null;
    busy = true;
    applyPresence();
    resetStatus.busy(`Resetting the password of ${user.name}…`);
    dc.relayout();

    try {
      const result = await crm.resetDeviceUserPassword(
        dc.deviceId,
        {
          userName: user.name,
          newPassword: resetPasswordInput.text,
          mustChangePassword: resetMustChangeCheck.checked,
          unlock: resetUnlockCheck.checked
        },
        dc.ctx.dispose.signal()
      );
      if (dc.ctx.cancelled()) return;

      if (!result.success) {
        resetStatus.error(result.error ?? 'The device refused to reset the password.');
        return;
      }

      openReset(null);
      done = `Reset the password of ${user.name}` + (result.wasUnlocked ? ' and unlocked the account.' : '.');
    } catch (error) {
      if (!dc.ctx.cancelled()) resetStatus.error(describeError(error));
    } finally {
      busy = false;
      applyPresence();
      dc.relayout();
    }

    if (done !== null && !dc.ctx.cancelled()) {
      await load();
      status.ok(`${done} ${status.label.text}`);
    }
  }

  // ---------------------------------------------------------------
  //  Changing groups
  // ---------------------------------------------------------------

  function openGroups(user: LocalUserDto | null): void {
    if (user !== null && !dc.isOnline()) {
      status.error('The device is offline.');
      dc.relayout();
      return;
    }
    groupsUser = user;
    groupsForm.visible = user !== null;
    groupsStatus.clear();
    if (user !== null) {
      openForm(false);
      openReset(null);
      openDelete(null);
      groupsTitle.text = `Groups of ${user.name}`;
      // Opens describing the account as the device has it, so Save with nothing changed is a no-op.
      groupsAdminCheck.checked = user.isAdministrator;
      groupsRdpCheck.checked = user.isRemoteDesktopUser;
    }
    dc.relayout();
  }

  async function saveGroups(): Promise<void> {
    if (busy || groupsUser === null) return;
    const user = groupsUser;
    const administrator = groupsAdminCheck.checked;
    const remoteDesktop = groupsRdpCheck.checked;
    if (administrator === user.isAdministrator && remoteDesktop === user.isRemoteDesktopUser) {
      groupsStatus.set('Nothing to change.');
      dc.relayout();
      return;
    }

    const machine = dc.device()?.name ?? 'this device';
    const changes: string[] = [];
    if (administrator !== user.isAdministrator)
      changes.push(administrator
        ? 'make it an ADMINISTRATOR, with full control of the machine and every other account on it'
        : 'remove it from Administrators, so it becomes a standard user');
    if (remoteDesktop !== user.isRemoteDesktopUser)
      changes.push(remoteDesktop ? 'allow it to sign in over Remote Desktop' : 'stop it signing in over Remote Desktop');
    const ok = await confirm(dc.ctx.desktop, {
      title: 'Change groups',
      message:
        `Change ${user.name} on ${machine}:\n\n- ${changes.join('\n- ')}\n\n` +
        'It takes effect the next time the account signs in.'
    });
    if (!ok || dc.ctx.cancelled()) return;

    let done: string | null = null;
    busy = true;
    applyPresence();
    groupsStatus.busy(`Changing the groups of ${user.name}…`);
    dc.relayout();

    try {
      const result = await crm.setDeviceUserGroups(
        dc.deviceId,
        { userName: user.name, administrator, remoteDesktop },
        dc.ctx.dispose.signal()
      );
      if (dc.ctx.cancelled()) return;
      if (!result.success) {
        groupsStatus.error(result.error ?? "The device refused to change the account's groups.");
        return;
      }
      openGroups(null);
      done = result.changed ? `Changed the groups of ${user.name}.` : `${user.name} already had those groups.`;
    } catch (error) {
      if (!dc.ctx.cancelled()) groupsStatus.error(describeError(error));
    } finally {
      busy = false;
      applyPresence();
      dc.relayout();
    }

    if (done !== null && !dc.ctx.cancelled()) {
      await load();
      status.ok(`${done} ${status.label.text}`);
    }
  }

  // ---------------------------------------------------------------
  //  Disabling and enabling
  // ---------------------------------------------------------------

  async function setEnabled(user: LocalUserDto, enabled: boolean): Promise<void> {
    if (busy) return;
    if (!dc.isOnline()) {
      status.error('The device is offline.');
      dc.relayout();
      return;
    }

    const machine = dc.device()?.name ?? 'this device';
    const signedIn = (dc.device()?.presence?.loggedOnUser ?? '').toLowerCase().endsWith('\\' + user.name.toLowerCase())
      || (dc.device()?.presence?.loggedOnUser ?? '').toLowerCase() === user.name.toLowerCase();
    const ok = await confirm(dc.ctx.desktop, enabled
      ? { title: 'Enable user', message: `Enable ${user.name} on ${machine}? It will be able to sign in again.` }
      : {
          title: 'Disable user',
          message:
            `Disable ${user.name} on ${machine}?\n\nIt can no longer sign in, and anything running as it - a ` +
            'scheduled task, a service - stops working at its next start. ' +
            (signedIn
              ? `${user.name} is signed in right now: Windows lets the session carry on until they sign out.`
              : 'Anyone already signed in as it stays signed in until they sign out.')
        });
    if (!ok || dc.ctx.cancelled()) return;

    let done: string | null = null;
    busy = true;
    applyPresence();
    status.busy(`${enabled ? 'Enabling' : 'Disabling'} ${user.name}…`);
    dc.relayout();

    try {
      const result = await crm.setDeviceUserEnabled(dc.deviceId, { userName: user.name, enabled }, dc.ctx.dispose.signal());
      if (dc.ctx.cancelled()) return;
      if (!result.success) {
        status.error(result.error ?? `The device refused to ${enabled ? 'enable' : 'disable'} the account.`);
        return;
      }
      done = result.changed
        ? `${enabled ? 'Enabled' : 'Disabled'} ${user.name}.`
        : `${user.name} was already ${enabled ? 'enabled' : 'disabled'}.`;
    } catch (error) {
      if (!dc.ctx.cancelled()) status.error(describeError(error));
    } finally {
      busy = false;
      applyPresence();
      dc.relayout();
    }

    if (done !== null && !dc.ctx.cancelled()) {
      await load();
      status.ok(`${done} ${status.label.text}`);
    }
  }

  function applyPresence(): void {
    const online = dc.isOnline();
    refreshButton.enabled = online && !busy;
    newButton.enabled = online && !busy;
    createButton.enabled = online && !busy;
    resetButton.enabled = online && !busy;
    deleteButton.enabled = online && !busy;
    groupsButton.enabled = online && !busy;
  }

  applyPresence();

  return {
    panel,
    onShow() {
      if (loaded || busy || !dc.isOnline()) return;
      void load();
    },
    applyPresence
  };
}
